import { IconButton, TextField } from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'
import { Box } from '@labrav/react-components'
import React, { useCallback, useMemo } from 'react'
import { KeyValue } from '../../../modules/privateOffer/privateOfferCreation/types'
import { MetaData } from '../../../modules/privateOffer/types'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import HighlightOffIcon from '@mui/icons-material/HighlightOffOutlined'
import { AddUpdateButton } from '../../../common/components/AddUpdateButton'
import { useStyles } from './MetaDataComponent.styles'
import { isEmpty } from 'lodash'
import { getRandom } from '@labrav/common'
import CancelIcon from '@mui/icons-material/Cancel'

export type MetaDataComponentProps = {
  metaData: MetaData[]
  updateMetaData: (index: number, key: string) => (data: KeyValue) => void
  deleteMetaDataRow: (index: number, key: string) => () => void
  addMetaDataRows: (key: string) => void
  errors: Array<Record<string, unknown>>
  heading?: string
  tooltipLabel?: string
  isResaleAuthorization?: boolean
}
export const MetaDataComponent = React.memo(
  ({
    metaData,
    updateMetaData,
    deleteMetaDataRow,
    addMetaDataRows,
    errors,
    heading,
    tooltipLabel,
    isResaleAuthorization,
  }: MetaDataComponentProps) => {
    const updateMetaDataRows = useCallback(
      () => addMetaDataRows('metaData'),
      [metaData]
    )
    const { classes } = useStyles()
    return (
      <Box
        heading={heading || 'Custom metadata'}
        subHeading={'Details'}
        tooltipLabel={
          tooltipLabel || 'Attaching any custom key-values to the offer'
        }
        icon={<InfoOutlined className={classes.infoIcon} />}
      >
        <div className={classes.metaDataContainer} data-testid="metadata-div">
          {metaData.map((data, index) => (
            <MetaDataRow
              key={`metadata-${index}`}
              metaData={data}
              index={index}
              updateMetaData={updateMetaData(index, 'metaData')}
              deleteMetaDataRow={deleteMetaDataRow(index, 'metaData')}
              metaDataLength={metaData.length}
              error={errors[index]}
              isResaleAuthorization={isResaleAuthorization}
            />
          ))}
          <div style={{ marginTop: '24px' }}>
            <AddUpdateButton
              label={'Add'}
              disabled={false}
              type={'add-metadata'}
              icon={AddCircleOutlineIcon}
              onClick={updateMetaDataRows}
            />
          </div>
        </div>
      </Box>
    )
  }
)

export type MetaDataRowsProps = {
  index: number
  metaDataLength: number
  metaData: MetaData
  updateMetaData: (data: KeyValue) => void
  deleteMetaDataRow: () => void
  error: Record<string, unknown>
  isResaleAuthorization?: boolean
}
export const MetaDataRow = ({
  index,
  metaDataLength,
  metaData,
  updateMetaData,
  deleteMetaDataRow,
  error,
  isResaleAuthorization,
}: MetaDataRowsProps) => {
  const randomKey = useMemo(getRandom, [index, metaDataLength])
  const handleInputChange = useCallback(
    (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      updateMetaData({ key, value: event.target.value as string })
    },
    [metaData]
  )
  const { classes } = useStyles()
  return (
    <div
      className={classes.updateRow}
      data-testid="metadata-row"
      key={`${randomKey}-${index}`}
    >
      <TextField
        id="key"
        label="Key"
        variant="standard"
        key={'Key'}
        defaultValue={metaData.key}
        className={classes.textField}
        onChange={handleInputChange('key')}
        error={!isEmpty(error?.key as string)}
        helperText={(error?.key as string) || ''}
        slotProps={{
          input: { inputProps: { 'data-testid': 'metadata-key' } },
        }}
      />
      <TextField
        id="Value"
        label="Value"
        variant="standard"
        key={'Value'}
        defaultValue={metaData.value}
        className={classes.textField}
        onChange={handleInputChange('value')}
        error={!isEmpty(error?.value as string)}
        helperText={(error?.value as string) || ''}
        slotProps={{
          input: { inputProps: { 'data-testid': 'metadata-value' } },
        }}
      />
      <IconButton
        className={classes.closeIconButton}
        onClick={deleteMetaDataRow}
        data-testid="cancel-button"
        size="large"
      >
        {!isResaleAuthorization ? (
          <HighlightOffIcon className={classes.closeIcon} />
        ) : (
          <CancelIcon fontSize="small" />
        )}
      </IconButton>
    </div>
  )
}
